import { Form, Input, Select, Row, Col, Typography, Divider } from 'antd';
import { ITEM_STATUSES, humaniseEnum } from '../../studioConstants';

const { TextArea } = Input;
const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Step 1 — Basic Identity.
//
// Identity, descriptions, optional SKU/barcode, status, remarks. All
// fields here are stored on the slim core inventory_items row — no
// extension fields. Item code is uppercased automatically.
// ─────────────────────────────────────────────────────────────────

export default function BasicIdentityStep({ disabled }) {
  return (
    <div>
      <h2 className="erp-studio-section-title">Basic identity</h2>
      <p className="erp-studio-section-help">
        Identify the item with a code, a clear name and optional descriptions. These fields
        appear on every invoice, dispatch note and report.
      </p>

      <Row gutter={[16, 0]}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Item code"
            name="itemCode"
            normalize={(v) => (v === undefined || v === null) ? v : String(v).toUpperCase()}
            rules={[
              { required: true, message: 'Item code is required.' },
              { max: 40, message: 'Max 40 characters.' },
              {
                pattern: /^[A-Z0-9_-]+$/,
                message: 'A–Z, 0–9, hyphen and underscore only.'
              }
            ]}
          >
            <Input placeholder="e.g. GLASS-4MM-CL" disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={16}>
          <Form.Item
            label="Item name"
            name="itemName"
            rules={[
              { required: true, message: 'Item name is required.' },
              { max: 200, message: 'Max 200 characters.' }
            ]}
          >
            <Input placeholder="e.g. 4MM ANNEALED PLAIN CLEAR GLASS" disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Form.Item label="Item description" name="itemDesc" rules={[{ max: 4000 }]}>
            <TextArea autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder="Long-form description used on quotations or sales orders."
              disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Form.Item label="Stock description" name="stockDesc" rules={[{ max: 400 }]}>
            <Input placeholder="Internal stock-card line." disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Label description" name="labelDesc" rules={[{ max: 200 }]}>
            <Input placeholder="What appears on the printed label." disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>Codes & status</Text>

      <Row gutter={[16, 0]} style={{ marginTop: 8 }}>
        <Col xs={24} md={8}>
          <Form.Item
            label="SKU code"
            name="skucode"
            normalize={(v) => (v === undefined || v === null || v === '') ? v : String(v).toUpperCase()}
            rules={[
              { max: 40, message: 'Max 40 characters.' },
              {
                pattern: /^[A-Z0-9_-]*$/,
                message: 'A–Z, 0–9, hyphen and underscore only.'
              }
            ]}
          >
            <Input placeholder="Optional — internal SKU." disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Barcode"
            name="barcode"
            rules={[{ max: 80, message: 'Max 80 characters.' }]}
          >
            <Input placeholder="Optional — UPC / EAN / QR payload." disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Status"
            name="status"
            tooltip="‘Discontinued’ retires an item without losing history."
          >
            <Select
              disabled={disabled}
              options={ITEM_STATUSES.map((s) => ({ value: s, label: humaniseEnum(s) }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Form.Item label="Remarks" name="remarks" rules={[{ max: 2000 }]}>
            <TextArea autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder="Internal notes — never printed."
              disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
}
